import { useLayoutEffect, useRef, useState } from 'react'

const CLIP_SECONDS = 5
const TICK_MS = 200
const PLAYBACK_SEEK_TOLERANCE_SECONDS = 0.35
const CLIP_END_TOLERANCE_SECONDS = 0.05

const getUniqueSources = (sources: string[]): string[] => {
  return Array.from(new Set(sources.filter(Boolean)))
}

const advancePlaybackIndexes = (
  currentSourceIndex: number,
  sourceCount: number,
  setClipIndex: (updater: (value: number) => number) => void
): number => {
  const next = (currentSourceIndex + 1) % sourceCount
  if (next === 0) {
    setClipIndex(value => value + 1)
  }
  return next
}

export const useProcessingBackgroundVideoPlayback = (parameters: { isPaused: boolean; sources: string[] }) => {
  const { isPaused, sources } = parameters
  const videoReference = useRef<HTMLVideoElement | null>(null)
  const exhaustedSourceIndexesReference = useRef(new Set<number>())
  const exhaustedClipIndexReference = useRef<number | undefined>(undefined)
  const [sourceIndex, setSourceIndex] = useState(0)
  const [clipIndex, setClipIndex] = useState(0)
  const uniqueSources = getUniqueSources(sources)
  const sourcesKey = uniqueSources.join('|')
  const activeSource = uniqueSources[sourceIndex] ?? uniqueSources[0]
  const sourceCount = uniqueSources.length

  useLayoutEffect(() => {
    exhaustedSourceIndexesReference.current.clear()
    exhaustedClipIndexReference.current = undefined
    setSourceIndex(0)
    setClipIndex(0)
  }, [sourcesKey])

  useLayoutEffect(() => {
    const video = videoReference.current
    if (!video || !activeSource) {
      return
    }

    if (isPaused) {
      video.pause()
      return
    }

    const startOffset = clipIndex * CLIP_SECONDS
    const applyClipWindow = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : undefined
      if (duration !== undefined && duration > 0 && startOffset >= duration) {
        if (exhaustedClipIndexReference.current !== clipIndex) {
          exhaustedClipIndexReference.current = clipIndex
          exhaustedSourceIndexesReference.current.clear()
        }

        const exhaustedSourceIndexes = exhaustedSourceIndexesReference.current
        exhaustedSourceIndexes.add(sourceIndex)
        if (exhaustedSourceIndexes.size === sourceCount) {
          exhaustedSourceIndexes.clear()
          exhaustedClipIndexReference.current = undefined
          setClipIndex(0)
          setSourceIndex(0)
        } else {
          const nextSourceIndex = (sourceIndex + 1) % sourceCount
          if (nextSourceIndex === 0) {
            setClipIndex(value => value + 1)
          }
          setSourceIndex(nextSourceIndex)
        }
        return
      }

      if (Math.abs(video.currentTime - startOffset) > PLAYBACK_SEEK_TOLERANCE_SECONDS) {
        video.currentTime = startOffset
      }
      video.play().catch(() => undefined)
    }

    if (video.src !== activeSource && !video.currentSrc.includes(activeSource)) {
      video.src = activeSource
      video.load()
    }

    const handleLoaded = () => {
      applyClipWindow()
    }

    video.addEventListener('loadedmetadata', handleLoaded)
    if (video.readyState >= 1) {
      applyClipWindow()
    }

    const intervalId = globalThis.setInterval(() => {
      const duration = Number.isFinite(video.duration) ? video.duration : undefined
      const endOffset =
        duration !== undefined && duration > 0
          ? Math.min(startOffset + CLIP_SECONDS, duration)
          : startOffset + CLIP_SECONDS

      if (video.ended || video.currentTime >= endOffset - CLIP_END_TOLERANCE_SECONDS) {
        setSourceIndex(current => advancePlaybackIndexes(current, sourceCount, setClipIndex))
      }
    }, TICK_MS)

    return () => {
      video.removeEventListener('loadedmetadata', handleLoaded)
      globalThis.clearInterval(intervalId)
    }
  }, [activeSource, clipIndex, isPaused, sourceCount, sourceIndex])

  return {
    activeSource,
    videoReference,
  }
}
